import type { DetectedPlugin } from './types';
import { parseAbleton } from './parsers/ableton';
import { parseReaper } from './parsers/reaper';
import { parseBitwig } from './parsers/bitwig';
import { parseStudioOne } from './parsers/studioOne';
import { scanVst2Ids } from './heuristic/vst2IdScanner';
import { extractStrings } from './heuristic/stringExtractor';
import { matchPluginNames } from './heuristic/pluginMatcher';

export type { DetectedPlugin };

/**
 * Deduplicate plugin detections.
 * Key = lowercase(name) + "|" + lowercase(pluginType|unknown) + "|" + lowercase(vst2Id|"")
 * Keep the detection with the highest confidence.
 */
function deduplicatePlugins(plugins: DetectedPlugin[]): DetectedPlugin[] {
  const map = new Map<string, DetectedPlugin>();

  for (const p of plugins) {
    const key = [
      p.name.toLowerCase(),
      (p.pluginType ?? 'unknown').toLowerCase(),
      (p.vst2Id ?? '').toLowerCase(),
    ].join('|');

    const existing = map.get(key);
    if (!existing || p.confidence > existing.confidence) {
      map.set(key, p);
    }
  }

  return Array.from(map.values());
}

function getExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.slice(dot).toLowerCase() : '';
}

/**
 * Main detection entry point.
 *
 * Runs tier-1 structured parser (when applicable), then always runs
 * tier-2 VST2 ID scan and tier-3 string heuristic.  Results are merged
 * and deduplicated.
 *
 * @param bytes - Raw file bytes (Uint8Array or ArrayBuffer)
 * @param fileName - File name including extension (used to select parser)
 * @returns Deduplicated list of detected plugins
 */
export async function detectPlugins(
  bytes: ArrayBuffer | Uint8Array,
  fileName: string
): Promise<DetectedPlugin[]> {
  const uint8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const ext = getExtension(fileName);

  const all: DetectedPlugin[] = [];

  // --- Tier 1: Structured parser ---
  try {
    switch (ext) {
      case '.als': {
        const structured = parseAbleton(uint8);
        all.push(...structured);
        break;
      }
      case '.rpp': {
        const structured = parseReaper(uint8);
        all.push(...structured);
        break;
      }
      case '.bwproject': {
        const structured = await parseBitwig(uint8);
        all.push(...structured);
        break;
      }
      case '.song':
      case '.multitrack': {
        const structured = await parseStudioOne(uint8);
        all.push(...structured);
        break;
      }
      // .flp (FL Studio), .cpr (Cubase), .ptf/.ptx (Pro Tools): no structured parser yet
      default:
        break;
    }
  } catch (e) {
    // Structured parse failure is non-fatal; heuristics still run
    console.warn(
      `[dawPluginDetection] Structured parse failed for ${fileName}:`,
      e
    );
  }

  // --- Tier 2: VST2 ID binary scan ---
  try {
    const vst2Found = scanVst2Ids(uint8);
    all.push(...vst2Found);
  } catch (e) {
    console.warn('[dawPluginDetection] VST2 ID scan failed:', e);
  }

  // --- Tier 3: String heuristic ---
  try {
    const strings = extractStrings(uint8);
    const heuristicFound = matchPluginNames(strings);
    all.push(...heuristicFound);
  } catch (e) {
    console.warn('[dawPluginDetection] String heuristic failed:', e);
  }

  return deduplicatePlugins(all);
}
